import React, { useMemo, useState } from 'react';
import { Alert, Button, Card, Modal, Spin, Typography } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { PDFViewer } from '@react-pdf/renderer';
import { ActionFlowRequestDTO, AppapproDTO } from '@/shared/api/model';
import { useAuthUser } from '@/modules/auth/context/AuthContext';
import { useCompany } from '@/hooks/company/use-company';
import { useProductPropertiesById } from '@/modules/loan-product-settings/hooks/useProductProperties';
import { useGenerateLoanScheduleFromConfig } from '@/shared/api/client/loan-schedule';
import useLoanFormTemplate from '../hooks/useLoanFormTemplate';
import SmartLoanApplicationDocument from './SmartLoanApplicationDocument';

const { Text } = Typography;

interface LoanApplicationFormViewProps {
    data?: ActionFlowRequestDTO;
    formikProps?: any;
}

const LoanApplicationFormView: React.FC<LoanApplicationFormViewProps> = ({ data, formikProps }) => {
    const [open, setOpen] = useState(false);
    const authUser = useAuthUser();
    const { data: companyData } = useCompany();
    const values = formikProps?.values || data || {};
    const valueData: AppapproDTO = values.valueData || {};
    const glNo = valueData.glNo || '';
    const amount = Number(valueData.recommendedLoanAmt || valueData.loanAmount || 0);
    const period = Number(valueData.recommendedLnPeriod || valueData.lnPeriod || 0);
    const { data: productPropertiesResponse, isLoading: isProductLoading } = useProductPropertiesById(glNo || undefined);
    const productProperties = productPropertiesResponse?.data as any;
    const { formConfig } = useLoanFormTemplate(productProperties);
    const previewFormConfig = useMemo(() => {
        if (!formConfig) {
            return {
                header: {
                    showLogo: true,
                    formTitle: 'LOAN APPLICATION FORM',
                },
                branding: {
                    logoUrl: '/public/appearance/logo',
                    watermarkUrl: '/public/appearance/logo',
                    showWatermark: true,
                },
            } as any;
        }

        return {
            ...formConfig,
            header: {
                ...(formConfig.header || {}),
                showLogo: formConfig.header?.showLogo ?? true,
            },
            branding: {
                ...(formConfig.branding || {}),
                logoUrl: formConfig.branding?.logoUrl || '/public/appearance/logo',
                watermarkUrl: formConfig.branding?.watermarkUrl || formConfig.branding?.logoUrl || '/public/appearance/logo',
                showWatermark: formConfig.branding?.showWatermark ?? true,
            },
        } as any;
    }, [formConfig]);
    const { data: scheduleResponse, isLoading: isScheduleLoading } = useGenerateLoanScheduleFromConfig(
        { glNo, amount, period },
        { query: { enabled: !!glNo && amount > 0 && period > 0 } }
    );
    const scheduleData = scheduleResponse?.data;

    return (
        <Card title="Loan Application Form" style={{ marginBottom: 16 }}>
            <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="Application Form Preview"
                description="Preview the generated loan application form using the selected customer, product, and loan parameters from the simulator."
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div>
                    <Text type="secondary">Product</Text>
                    <div>{productProperties?.productName || valueData.productName || glNo || 'Not selected'}</div>
                </div>
                <div>
                    <Text type="secondary">Amount</Text>
                    <div>{amount > 0 ? amount.toLocaleString() : '-'}</div>
                </div>
                <div>
                    <Text type="secondary">Period</Text>
                    <div>{period > 0 ? `${period} months` : '-'}</div>
                </div>
                <Button
                    type="primary"
                    icon={<EyeOutlined />}
                    disabled={!scheduleData || isProductLoading || isScheduleLoading}
                    onClick={() => setOpen(true)}
                >
                    Preview Application Form
                </Button>
            </div>
            {(isProductLoading || isScheduleLoading) ? (
                <div style={{ textAlign: 'center', padding: 24 }}>
                    <Spin />
                </div>
            ) : null}
            <Modal
                title="Loan Application Form Preview"
                open={open}
                onCancel={() => setOpen(false)}
                footer={null}
                width="80%"
                style={{ top: 20 }}
                bodyStyle={{ height: '80vh', padding: 0 }}
            >
                {scheduleData ? (
                    <PDFViewer width="100%" height="100%" style={{ border: 'none' }}>
                        <SmartLoanApplicationDocument
                            user={authUser}
                            company={companyData}
                            product={productProperties || { productName: valueData.productName || '', glNo }}
                            amount={amount ? amount.toString() : undefined}
                            period={period ? period.toString() : undefined}
                            scheduleData={scheduleData}
                            formConfig={previewFormConfig}
                        />
                    </PDFViewer>
                ) : (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <Spin />
                    </div>
                )}
            </Modal>
        </Card>
    );
};

export default LoanApplicationFormView;
